import React from 'react';
import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

interface EmptyStateProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Single primary action shorthand. */
  actionLabel?: string;
  onAction?: () => void;
  /** Any control, for callers that need a link or a secondary button. */
  action?: React.ReactNode;
  icon?: React.ReactNode;
  /**
   * `page` is the dashed, tinted block for a whole empty list. `compact`
   * is the quiet centred text for an empty section inside a card, which
   * pages used to write inline as "No X yet".
   */
  size?: 'page' | 'compact';
  /** Layout overrides for the root, for instance a different vertical padding. */
  sx?: SxProps<Theme>;
}

/**
 * The one empty state. Two components and roughly thirty inline "No X
 * found" blocks used to do this job with different spacing and copy.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  actionLabel,
  onAction,
  action,
  icon,
  size = 'page',
  sx,
}) => {
  const compact = size === 'compact';
  const control = action ?? (actionLabel && onAction ? (
    <Button variant={compact ? 'text' : 'contained'} size={compact ? 'small' : 'medium'} onClick={onAction}>
      {actionLabel}
    </Button>
  ) : null);

  const base: SxProps<Theme> = compact
    ? { py: 3, px: 2, textAlign: 'center' }
    : {
        py: 7,
        px: 3,
        textAlign: 'center',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'grey.50',
      };

  return (
    <Box sx={[base, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}>
      {icon && (
        <Box sx={{ color: 'text.disabled', mb: 1, '& svg': { fontSize: compact ? 28 : 40 } }}>{icon}</Box>
      )}
      <Typography variant={compact ? 'body2' : 'h6'} color={compact ? 'text.secondary' : 'text.primary'}>
        {title}
      </Typography>
      {description && (
        <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary" component="p" sx={{ mt: compact ? 0.5 : 1 }}>
          {description}
        </Typography>
      )}
      {control && <Box sx={{ mt: compact ? 1 : 2 }}>{control}</Box>}
    </Box>
  );
};
